import { FormEvent, useState } from 'react';
import type { CreateContractPayload, PaymentTerm } from './adminContractApi';
import {
  CONTRACT_TYPES,
  PROVIDER_INFO,
  formatAmountInput,
  splitPhoneDigits,
} from './adminContractUtils';
import './AdminContractManage.scss';

export type AdminContractFormMode = 'create' | 'edit';

export type AdminContractFormProps = {
  mode: AdminContractFormMode;
  form: CreateContractPayload;
  error: string | null;
  submitting: boolean;
  onBack: () => void;
  onFieldChange: <K extends keyof CreateContractPayload>(
    key: K,
    value: CreateContractPayload[K],
  ) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
};

const EMPTY_PAYMENT_TERM: PaymentTerm = { category: '', amount: '', condition: '' };

export default function AdminContractForm({
  mode,
  form,
  error,
  submitting,
  onBack,
  onFieldChange,
  onSubmit,
}: AdminContractFormProps) {
  const paymentTerms = form.payment_terms?.length ? form.payment_terms : [EMPTY_PAYMENT_TERM];
  const [phoneParts, setPhoneParts] = useState<[string, string, string]>(() =>
    splitPhoneDigits(form.phone),
  );

  const updatePhonePart = (part: 0 | 1 | 2, raw: string, maxLength: number) => {
    const digits = raw.replace(/\D/g, '').slice(0, maxLength);
    const next: [string, string, string] = [...phoneParts];
    next[part] = digits;
    setPhoneParts(next);
    onFieldChange('phone', next.filter(Boolean).join('-'));
  };

  const updatePaymentTerm = (index: number, key: keyof PaymentTerm, value: string) => {
    const next = paymentTerms.map((row, i) => (i === index ? { ...row, [key]: value } : row));
    onFieldChange('payment_terms', next);
  };

  const addPaymentTerm = () => {
    onFieldChange('payment_terms', [...paymentTerms, { ...EMPTY_PAYMENT_TERM }]);
  };

  const removePaymentTerm = (index: number) => {
    const next = paymentTerms.filter((_, i) => i !== index);
    onFieldChange('payment_terms', next.length ? next : [{ ...EMPTY_PAYMENT_TERM }]);
  };

  return (
    <div className="admin-contract-manage admin-contract-manage--form-page">
      <header className="admin-contract-manage__form-header">
        <button
          type="button"
          className="admin-contract-manage__back-btn"
          onClick={onBack}
          disabled={submitting}
        >
          ← 목록으로
        </button>
        <div>
          <h2 className="admin-contract-manage__title">
            {mode === 'edit' ? '계약 수정' : '계약 등록'}
          </h2>
          <p className="admin-contract-manage__subtitle">
            고객이 홈페이지에서 조회할 수 있도록 계약 내용을 입력해 주세요.
            고객명과 전화번호가 조회 키입니다.
          </p>
        </div>
      </header>

      {error && <p className="admin-contract-manage__error">{error}</p>}

      <form className="admin-contract-manage__panel" onSubmit={onSubmit}>
        <div className="admin-contract-manage__form-grid">
          <div className="admin-contract-manage__section-title">계약 개요</div>

          <div className="admin-contract-manage__field">
            <label htmlFor="admin-contract-contract-name">계약명</label>
            <input
              id="admin-contract-contract-name"
              type="text"
              value={form.contract_name}
              onChange={(e) => onFieldChange('contract_name', e.target.value)}
              placeholder="예: OO 플랫폼 구축 계약"
            />
          </div>

          <div className="admin-contract-manage__field">
            <label htmlFor="admin-contract-product-name">상품명</label>
            <input
              id="admin-contract-product-name"
              type="text"
              value={form.product_name}
              onChange={(e) => onFieldChange('product_name', e.target.value)}
              placeholder="예: 예약 플랫폼 스탠다드"
            />
          </div>

          <div className="admin-contract-manage__field">
            <label htmlFor="admin-contract-customer">
              고객명 <span className="admin-contract-manage__required">*</span>
            </label>
            <input
              id="admin-contract-customer"
              type="text"
              value={form.customer_name}
              onChange={(e) => onFieldChange('customer_name', e.target.value)}
              placeholder="홍길동"
              required
            />
          </div>

          <div className="admin-contract-manage__field">
            <label htmlFor="admin-contract-phone-1">
              전화번호 <span className="admin-contract-manage__required">*</span>
            </label>
            <div className="admin-contract-manage__phone-group">
              <input
                id="admin-contract-phone-1"
                type="tel"
                inputMode="numeric"
                value={phoneParts[0]}
                onChange={(e) => updatePhonePart(0, e.target.value, 4)}
                placeholder="010"
                maxLength={4}
                required
              />
              <span className="admin-contract-manage__phone-dash">-</span>
              <input
                type="tel"
                inputMode="numeric"
                value={phoneParts[1]}
                onChange={(e) => updatePhonePart(1, e.target.value, 4)}
                placeholder="0000"
                maxLength={4}
                required
              />
              <span className="admin-contract-manage__phone-dash">-</span>
              <input
                type="tel"
                inputMode="numeric"
                value={phoneParts[2]}
                onChange={(e) => updatePhonePart(2, e.target.value, 4)}
                placeholder="0000"
                maxLength={4}
                required
              />
            </div>
          </div>

          <div className="admin-contract-manage__field">
            <label htmlFor="admin-contract-type">
              계약 유형 <span className="admin-contract-manage__required">*</span>
            </label>
            <select
              id="admin-contract-type"
              value={form.contract_type}
              onChange={(e) => onFieldChange('contract_type', e.target.value)}
            >
              {CONTRACT_TYPES.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.title}
                </option>
              ))}
            </select>
          </div>

          <div className="admin-contract-manage__section-title">특이사항</div>

          <div className="admin-contract-manage__field admin-contract-manage__field--wide">
            <textarea
              id="admin-contract-special-notes"
              aria-label="특이사항"
              value={form.special_notes}
              onChange={(e) => onFieldChange('special_notes', e.target.value)}
              placeholder="계약과 관련해 별도로 협의된 사항이 있다면 입력해 주세요."
              rows={4}
            />
          </div>

          <div className="admin-contract-manage__section-title">계약 비용</div>

          <div className="admin-contract-manage__field">
            <label htmlFor="admin-contract-build-cost">구축비</label>
            <input
              id="admin-contract-build-cost"
              type="text"
              inputMode="numeric"
              value={form.build_cost}
              onChange={(e) => onFieldChange('build_cost', formatAmountInput(e.target.value))}
              placeholder="예: 5,000,000"
            />
          </div>

          <div className="admin-contract-manage__field">
            <label htmlFor="admin-contract-build-period">구축기간</label>
            <input
              id="admin-contract-build-period"
              type="text"
              value={form.build_period}
              onChange={(e) => onFieldChange('build_period', e.target.value)}
              placeholder="예: 계약일로부터 8주"
            />
          </div>

          <div className="admin-contract-manage__field">
            <label htmlFor="admin-contract-maintenance-fee">월 유지보수비</label>
            <input
              id="admin-contract-maintenance-fee"
              type="text"
              inputMode="numeric"
              value={form.maintenance_fee}
              onChange={(e) => onFieldChange('maintenance_fee', formatAmountInput(e.target.value))}
              placeholder="예: 200,000"
            />
          </div>

          <div className="admin-contract-manage__field">
            <label htmlFor="admin-contract-maintenance-period">유지보수기간</label>
            <input
              id="admin-contract-maintenance-period"
              type="text"
              value={form.maintenance_period}
              onChange={(e) => onFieldChange('maintenance_period', e.target.value)}
              placeholder="예: 오픈일로부터 12개월"
            />
          </div>

          <div className="admin-contract-manage__field admin-contract-manage__field--wide">
            <label>지불방법</label>
            <div className="admin-contract-manage__payment-table-wrap">
              <table className="admin-contract-manage__payment-table">
                <thead>
                  <tr>
                    <th>구분</th>
                    <th>금액</th>
                    <th>지급조건</th>
                    <th aria-hidden="true" />
                  </tr>
                </thead>
                <tbody>
                  {paymentTerms.map((row, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          type="text"
                          value={row.category}
                          onChange={(e) => updatePaymentTerm(index, 'category', e.target.value)}
                          placeholder="예: 착수금"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={row.amount}
                          onChange={(e) =>
                            updatePaymentTerm(index, 'amount', formatAmountInput(e.target.value))
                          }
                          placeholder="예: 1,500,000"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={row.condition}
                          onChange={(e) => updatePaymentTerm(index, 'condition', e.target.value)}
                          placeholder="예: 계약 체결 시"
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="admin-contract-manage__payment-remove-btn"
                          onClick={() => removePaymentTerm(index)}
                          disabled={paymentTerms.length <= 1}
                        >
                          삭제
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              className="admin-contract-manage__payment-add-btn"
              onClick={addPaymentTerm}
            >
              + 지불 항목 추가
            </button>
          </div>

          <div className="admin-contract-manage__section-title">
            고객(갑) 정보
            <span className="admin-contract-manage__section-hint">
              연락처는 상단 전화번호가 사용됩니다.
            </span>
          </div>

          <div className="admin-contract-manage__field">
            <label htmlFor="admin-contract-representative">대표자</label>
            <input
              id="admin-contract-representative"
              type="text"
              value={form.representative}
              onChange={(e) => onFieldChange('representative', e.target.value)}
              placeholder="미입력 시 고객명과 동일"
            />
          </div>

          <div className="admin-contract-manage__field">
            <label htmlFor="admin-contract-company">상호명</label>
            <input
              id="admin-contract-company"
              type="text"
              value={form.company_name}
              onChange={(e) => onFieldChange('company_name', e.target.value)}
              placeholder="예: 예플랫 주식회사"
            />
          </div>

          <div className="admin-contract-manage__field">
            <label htmlFor="admin-contract-business">사업자등록번호(주민번호)</label>
            <input
              id="admin-contract-business"
              type="text"
              value={form.business_number}
              onChange={(e) => onFieldChange('business_number', e.target.value)}
              placeholder="000-00-00000"
            />
          </div>

          <div className="admin-contract-manage__field">
            <label htmlFor="admin-contract-email">이메일</label>
            <input
              id="admin-contract-email"
              type="email"
              value={form.email}
              onChange={(e) => onFieldChange('email', e.target.value)}
              placeholder="contact@example.com"
            />
          </div>

          <div className="admin-contract-manage__field">
            <label htmlFor="admin-contract-address">주소</label>
            <input
              id="admin-contract-address"
              type="text"
              value={form.address}
              onChange={(e) => onFieldChange('address', e.target.value)}
              placeholder="사업장 주소"
            />
          </div>

          <div className="admin-contract-manage__section-title">
            공급자(을) 정보
            <span className="admin-contract-manage__section-hint">
              모든 계약에 공통 적용되는 고정 정보입니다.
            </span>
          </div>

          <div className="admin-contract-manage__field admin-contract-manage__field--wide">
            <dl className="admin-contract-manage__provider-info">
              <div>
                <dt>상호명</dt>
                <dd>{PROVIDER_INFO.companyName}</dd>
              </div>
              <div>
                <dt>대표자</dt>
                <dd>{PROVIDER_INFO.representative}</dd>
              </div>
              <div>
                <dt>사업자등록번호</dt>
                <dd>{PROVIDER_INFO.businessNumber}</dd>
              </div>
              <div>
                <dt>연락처</dt>
                <dd>{PROVIDER_INFO.contact}</dd>
              </div>
              <div>
                <dt>주소</dt>
                <dd>{PROVIDER_INFO.address}</dd>
              </div>
              <div>
                <dt>계좌번호</dt>
                <dd>{PROVIDER_INFO.accountNumber}</dd>
              </div>
            </dl>
          </div>

          <div className="admin-contract-manage__section-title">계약 약관</div>

          <div className="admin-contract-manage__field admin-contract-manage__field--wide">
            <textarea
              id="admin-contract-terms"
              aria-label="계약 약관"
              value={form.terms_text}
              onChange={(e) => onFieldChange('terms_text', e.target.value)}
              rows={12}
            />
          </div>
        </div>

        <div className="admin-contract-manage__form-actions">
          <button
            type="button"
            className="admin-contract-manage__cancel-btn"
            onClick={onBack}
            disabled={submitting}
          >
            취소
          </button>
          <button
            type="submit"
            className="admin-contract-manage__submit-btn"
            disabled={submitting}
          >
            {submitting ? '저장 중...' : mode === 'edit' ? '수정 저장' : '계약 저장'}
          </button>
        </div>
      </form>
    </div>
  );
}
